import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { PhotoIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { ChatbotService, type VideoModelOut } from '../../client'
import { apiErrorMessage } from '../../lib/api'
import { useChat } from '../../providers/chat/context'
import Modal from '../ui/Modal'

type Props = { brandId: number; onClose: () => void }
type Slot = 'first' | 'last' | 'reference'

const PILL =
  'h-9 appearance-none rounded-full border border-stone-200 bg-white px-3 text-sm text-stone-700 transition hover:border-stone-300 focus:border-purple-400 focus:outline-none'

function Choice<T extends string | number>({
  label,
  value,
  options,
  format = String,
  onChange,
}: {
  label: string
  value: T
  options: T[]
  format?: (option: T) => string
  onChange: (value: T) => void
}) {
  return (
    <select
      aria-label={label}
      value={String(value)}
      onChange={(event) => onChange(options.find((o) => String(o) === event.target.value) ?? value)}
      className={PILL}
    >
      {options.map((option) => (
        <option key={String(option)} value={String(option)}>
          {format(option)}
        </option>
      ))}
    </select>
  )
}

function FrameTile({ label, url, onPick, onClear }: { label: string; url?: string; onPick: () => void; onClear?: () => void }) {
  return (
    <div className="relative">
      <button
        type="button"
        onClick={onPick}
        className="flex size-24 flex-col items-center justify-center gap-1 overflow-hidden rounded-xl border border-dashed border-stone-300 bg-white text-xs text-stone-500 transition hover:border-purple-400 hover:text-purple-700"
      >
        {url ? (
          <img src={url} alt={label} className="size-full object-cover" />
        ) : (
          <>
            <PlusIcon className="size-5" />
            {label}
          </>
        )}
      </button>
      {url && onClear && (
        <button
          type="button"
          aria-label={`Remove ${label}`}
          onClick={onClear}
          className="absolute -top-2 -right-2 flex size-6 items-center justify-center rounded-full bg-stone-900 text-white pointer-coarse:size-8"
        >
          <XMarkIcon className="size-3.5" />
        </button>
      )}
    </div>
  )
}

export default function GenerateVideoDialog({ brandId, onClose }: Props) {
  const navigate = useNavigate()
  const { balance, refreshThreads } = useChat()
  const [models, setModels] = useState<VideoModelOut[]>([])
  const [modelId, setModelId] = useState('')
  const [prompt, setPrompt] = useState('')
  const [aspectRatio, setAspectRatio] = useState('9:16')
  const [resolution, setResolution] = useState('720p')
  const [seconds, setSeconds] = useState(8)
  const [count, setCount] = useState(1)
  const [audio, setAudio] = useState(true)
  const [firstFrame, setFirstFrame] = useState<string>()
  const [lastFrame, setLastFrame] = useState<string>()
  const [references, setReferences] = useState<string[]>([])
  const [uploading, setUploading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [message, setMessage] = useState('')
  const fileInput = useRef<HTMLInputElement>(null)
  const slot = useRef<Slot>('first')

  useEffect(() => {
    ChatbotService.listVideoModels(brandId)
      .then((list) => {
        setModels(list)
        setModelId((list.find((m) => m.default) ?? list[0])?.id ?? '')
      })
      .catch((err) => setMessage(apiErrorMessage(err)))
  }, [brandId])

  const model = models.find((m) => m.id === modelId)

  function pickModel(id: string) {
    const next = models.find((m) => m.id === id)
    if (!next) return
    setModelId(id)
    if (!next.aspect_ratios.includes(aspectRatio)) setAspectRatio(next.aspect_ratios[0])
    if (!next.resolutions.includes(resolution)) setResolution(next.resolutions[0])
    if (!next.durations.includes(seconds)) setSeconds(next.durations[next.durations.length - 1])
    if (!next.last_frame) setLastFrame(undefined)
    setReferences((current) => current.slice(0, next.max_references))
  }

  function choose(target: Slot) {
    slot.current = target
    fileInput.current?.click()
  }

  async function upload(file: File | undefined) {
    if (!file) return
    setUploading(true)
    setMessage('')
    try {
      const { url } = await ChatbotService.uploadAsset(brandId, { file })
      if (slot.current === 'first') setFirstFrame(url)
      else if (slot.current === 'last') setLastFrame(url)
      else setReferences((current) => [...current, url])
    } catch (err) {
      setMessage(apiErrorMessage(err))
    } finally {
      setUploading(false)
      if (fileInput.current) fileInput.current.value = ''
    }
  }

  async function submit() {
    if (!model || !prompt.trim() || submitting) return
    setSubmitting(true)
    setMessage('')
    try {
      const thread = await ChatbotService.createVideoGeneration(brandId, {
        prompt: prompt.trim(),
        model: model.id,
        aspect_ratio: aspectRatio,
        resolution,
        seconds,
        count,
        audio,
        first_frame_url: firstFrame ?? null,
        last_frame_url: lastFrame ?? null,
        reference_image_urls: references,
      })
      await refreshThreads()
      onClose()
      void navigate(`/c/${thread.id}`)
    } catch (err) {
      setMessage(apiErrorMessage(err))
      setSubmitting(false)
    }
  }

  const estimate = model ? Math.ceil(model.credits_per_second * seconds * count * 100) / 100 : 0

  return (
    <Modal
      title="Generate Video with AI"
      width="max-w-3xl"
      padded={false}
      onClose={onClose}
      headerRight={
        balance && (
          <span className="text-xs text-stone-400">
            {balance.credits_used} / {balance.credits_total} credits used
          </span>
        )
      }
    >
      <div className="flex-1 space-y-6 overflow-y-auto px-7 py-6 [scrollbar-width:thin]">
        <div className="rounded-xl border border-stone-200 bg-white p-3 focus-within:border-purple-400">
          <textarea
            autoFocus
            value={prompt}
            onChange={(event) => setPrompt(event.target.value)}
            maxLength={2000}
            rows={5}
            placeholder="Describe the video you want... e.g., 'Slow push-in on a serum bottle on wet marble, soft morning light, droplets catching the sun'"
            className="w-full resize-none border-0 bg-transparent text-sm text-stone-900 placeholder:text-stone-400 focus:ring-0 focus:outline-none"
          />
          {model && (
            <div className="flex flex-wrap items-center gap-2 pt-2">
              <Choice
                label="Model"
                value={modelId}
                options={models.map((m) => m.id)}
                format={(id) => models.find((m) => m.id === id)?.label ?? id}
                onChange={pickModel}
              />
              <Choice label="Aspect ratio" value={aspectRatio} options={model.aspect_ratios} onChange={setAspectRatio} />
              <Choice label="Resolution" value={resolution} options={model.resolutions} onChange={setResolution} />
              <Choice label="Duration" value={seconds} options={model.durations} format={(s) => `${s}s`} onChange={setSeconds} />
              <Choice
                label="Versions"
                value={count}
                options={[1, 2, 3, 4]}
                format={(n) => `${n} version${n > 1 ? 's' : ''}`}
                onChange={setCount}
              />
              {model.audio && (
                <label className="flex h-9 items-center gap-2 rounded-full border border-stone-200 bg-white px-3 text-sm text-stone-700">
                  <input
                    type="checkbox"
                    checked={audio}
                    onChange={(event) => setAudio(event.target.checked)}
                    className="accent-purple-700"
                  />
                  Sound
                </label>
              )}
            </div>
          )}
        </div>

        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm font-medium text-stone-900">
            <PhotoIcon className="size-4 text-stone-400" />
            Frames and references
            <span className="font-normal text-stone-400">optional</span>
          </div>
          <div className="flex flex-wrap gap-3">
            <FrameTile label="Start frame" url={firstFrame} onPick={() => choose('first')} onClear={() => setFirstFrame(undefined)} />
            {model?.last_frame && (
              <FrameTile label="End frame" url={lastFrame} onPick={() => choose('last')} onClear={() => setLastFrame(undefined)} />
            )}
            {references.map((url, index) => (
              <FrameTile
                key={url}
                label={`Reference ${index + 1}`}
                url={url}
                onPick={() => undefined}
                onClear={() => setReferences((current) => current.filter((u) => u !== url))}
              />
            ))}
            {model && references.length < model.max_references && (
              <FrameTile label="Reference" onPick={() => choose('reference')} />
            )}
          </div>
          <p className="text-xs text-stone-500">
            The start frame opens the clip and the end frame closes it. References keep a person or product looking the same.
          </p>
          <input
            ref={fileInput}
            type="file"
            accept="image/*"
            hidden
            onChange={(event) => void upload(event.target.files?.[0])}
          />
        </div>

        {message && <p className="text-xs text-red-600">{message}</p>}
      </div>

      <div className="flex shrink-0 items-center justify-between gap-2 border-t border-stone-200 px-7 py-4">
        <span className="text-xs text-stone-500">{model && `About ${estimate} credits`}</span>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg bg-white px-4 py-2 text-sm font-medium text-stone-700 shadow-sm ring-1 ring-stone-300 transition ring-inset hover:bg-stone-50"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!model || !prompt.trim() || submitting || uploading}
            onClick={() => void submit()}
            className="rounded-lg bg-purple-900 px-5 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-purple-950 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting ? 'Starting...' : uploading ? 'Uploading...' : `Generate ${count} Video${count > 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </Modal>
  )
}
